// Texto por defecto de la página (specs/138-editor-contenido-paginas). Es la fuente del contenido
// mientras nadie lo edite en el panel; una edición guardada lo reemplaza por bloque.
//
// Landing de temporada que cuelga de /merchandising-corporativo (no es un servicio del menú).
// Búsquedas objetivo (Keyword Planner, Perú): «canastas navideñas corporativas» (170/mes de promedio,
// 720 en nov.–dic.), «regalos corporativos navidad» (140; 590 en nov.–dic.) y «regalos corporativos»
// (720; 1.300 en nov.). El SERP es de landings de vendedores de canastas: compite una página propia, no
// una sección. La agencia confirmó que vende canastas y packs navideños corporativos; el resto de lo que
// se afirma es lo confirmado para merchandising (diseño incluido, proveedores especializados, pedido
// mínimo según producto, envíos a todo el Perú). Sin precios: no hay una lista confirmada.
import type { ContenidoServicio } from "@/lib/page-content/tipos";

export const contenido: ContenidoServicio = {
  actualizado: "2026-11-01",
  eyebrow: "Canastas navideñas corporativas",
  h1: "Canastas navideñas corporativas y regalos de fin de año para empresas",
  intro:
    "Canastas navideñas y packs de fin de año para tus colaboradores y clientes, con la identidad de tu empresa. Te proponemos opciones según tu presupuesto, las personalizamos con tu marca y coordinamos la entrega en Lima y en todo el Perú.",
  description:
    "## Canastas navideñas para empresas: qué ofrecemos\n\nLa canasta navideña es el regalo de fin de año más esperado por los equipos en el Perú, y también una forma de agradecer a tus clientes. Armamos canastas y packs navideños corporativos a la medida de tu presupuesto y de quién los recibe: no es lo mismo el regalo para todo el equipo que el detalle para un cliente clave.\n\nTe proponemos varias opciones de contenido y presentación, con un costo por unidad claro, para que elijas la que mejor encaja con tu cantidad y tu fecha de entrega.\n\n## Personalizadas con tu marca\n\nUna canasta corporativa no es una canasta genérica con un sticker. Proponemos el diseño de la presentación con tu identidad visual —caja, empaque, tarjeta o mensaje de la empresa— y te mostramos cómo se verá antes de producir. Si quieres sumar artículos con tu logo, los integramos desde nuestro servicio de [merchandising corporativo](/merchandising-corporativo).\n\n## Packs y regalos corporativos de fin de año\n\nAdemás de la canasta tradicional, armamos packs navideños y regalos de fin de año para clientes y colaboradores: kits personalizados, regalos ejecutivos y combinaciones con merchandising de tu marca. Es una buena alternativa cuando el regalo es para clientes o cuando quieres que lo recuerden durante el año.\n\n## Cómo trabajamos tu pedido\n\n1. **Brief:** cantidad, presupuesto por unidad, a quién va dirigido y fecha de entrega.\n2. **Propuesta:** opciones de contenido y presentación, con el diseño de la personalización.\n3. **Aprobación:** revisas la propuesta y, si corresponde, una muestra antes de producir.\n4. **Producción y armado:** coordinamos con proveedores especializados y consolidamos todo el pedido.\n5. **Entrega:** en Lima o con envío a todo el Perú, según lo que acordemos.\n\n## Pide con anticipación\n\nEn noviembre y diciembre la demanda de canastas navideñas sube y los plazos se acortan. El pedido mínimo y el tiempo de preparación dependen del contenido y de la personalización; te los indicamos en la propuesta. Mientras antes nos escribas, más opciones podemos ofrecerte.\n\n## Completa el cierre de año de tu empresa\n\nSi además de los regalos vas a celebrar con tu equipo, también producimos [eventos corporativos](/eventos-corporativos) y fiestas de fin de año, con ideas como un [show navideño para empresas](/blog/show-navideno-para-empresas-lima-guia-completa) o una [hora loca](/blog/hora-loca-para-eventos-corporativos-guia-completa).\n\n[Solicita una cotización de canastas navideñas](/cotizar) indicando cantidad, presupuesto por unidad y fecha de entrega. Te enviamos una propuesta con opciones en menos de 72 horas.",
  descriptionLayout: "articulo" as const,
  includes: [
    "Opciones de canastas y packs según tu presupuesto",
    "Personalización con tu marca: presentación, tarjeta o mensaje",
    "Prueba o muestra antes de producir",
    "Armado y consolidación del pedido",
    "Entrega en Lima y envíos a todo el Perú",
  ],
  audience: [
    "Áreas de recursos humanos que preparan el regalo de fin de año",
    "Empresas que agradecen a sus clientes en Navidad",
    "Equipos comerciales con regalos para clientes clave",
    "Empresas con sedes en distintas ciudades del Perú",
  ],
  projectTypes: [
    {
      title: "Canastas para colaboradores",
      desc: "El regalo de Navidad para todo el equipo, en la cantidad y presupuesto que definas.",
    },
    {
      title: "Canastas para clientes",
      desc: "Una presentación más cuidada para agradecer a tus clientes clave.",
    },
    {
      title: "Packs navideños",
      desc: "Kits de fin de año con productos de temporada y la identidad de tu empresa.",
    },
    {
      title: "Regalos ejecutivos",
      desc: "Detalles para gerencia, directorio o clientes estratégicos.",
    },
    {
      title: "Canastas con merchandising",
      desc: "Combinadas con artículos personalizados con tu logo.",
    },
    {
      title: "Envíos a varias sedes",
      desc: "Pedidos para equipos repartidos en distintas ciudades del Perú.",
    },
  ],
  benefits: [
    {
      title: "Con tu marca, no genéricas",
      desc: "Diseñamos la presentación con tu identidad visual y te la mostramos antes de producir.",
    },
    {
      title: "Un solo responsable",
      desc: "Coordinamos proveedores, armado y entrega: tú recibes una propuesta y un pedido consolidado.",
    },
    {
      title: "Cierre de año completo",
      desc: "Si también celebras con tu equipo, producimos la fiesta de fin de año con el mismo equipo.",
    },
  ],
  faqs: [
    {
      q: "¿Hacen canastas navideñas para empresas?",
      a: "Sí. Armamos canastas y packs navideños corporativos para colaboradores y clientes, personalizados con la identidad de tu empresa.",
    },
    {
      q: "¿Se pueden personalizar con el logo de la empresa?",
      a: "Sí. Proponemos el diseño de la presentación —caja, empaque, tarjeta o mensaje— con tu marca y te lo mostramos antes de producir.",
    },
    {
      q: "¿Cuál es el pedido mínimo?",
      a: "Depende del contenido y de la personalización. Te lo indicamos en la propuesta junto con el costo por unidad.",
    },
    {
      q: "¿Hacen envíos a provincias?",
      a: "Sí, entregamos en Lima y enviamos a todo el Perú. Si tu equipo está en varias sedes, coordinamos la entrega en cada una.",
    },
    {
      q: "¿Con cuánta anticipación debo pedirlas?",
      a: "Lo antes posible: en noviembre y diciembre la demanda sube y los plazos se acortan. Escríbenos con la cantidad y la fecha de entrega y te confirmamos el plazo.",
    },
    {
      q: "¿Cuánto cuesta una canasta navideña corporativa?",
      a: "Depende del contenido, la presentación, la personalización, la cantidad y el envío. Te enviamos opciones con el costo por unidad en menos de 72 horas.",
    },
  ],
};
